import crypto from 'node:crypto';
import fs from 'node:fs';
import _ from '@env-spec/utils/my-dash';
import { getAutogeneratedBanner } from '../banners';
import {
  escapeBlockCommentEnd,
  IDENTIFIER_SAFE_KEY,
  type CoercedType,
  type ResolvedFieldType,
} from '../shared';

// keys with `.`/`-` are legal env keys but not valid TS identifiers — quote them so they stay typed
// and accessible via `ENV['MY-KEY']` (unlike Go/Rust/PHP/Python, TS can represent them, so we don't skip)
function tsPropertyKey(key: string): string {
  return IDENTIFIER_SAFE_KEY.test(key) ? key : JSON.stringify(key);
}

function getItemTsTypeString(coerced: CoercedType): string {
  if (coerced === 'number' || coerced === 'int') return 'number';
  if (coerced === 'boolean') return 'boolean';
  if (coerced === 'object') return 'Record<string, any>';
  if (typeof coerced === 'object' && 'enum' in coerced) {
    if (!coerced.enum.length) return 'never';
    return coerced.enum.map((option) => JSON.stringify(option)).join(' | ');
  }
  if (typeof coerced === 'object' && 'arrayOf' in coerced) {
    const inner = getItemTsTypeString(coerced.arrayOf);
    // parenthesize compound inner types so `("a" | "b")[]` doesn't become `"a" | "b"[]`
    return /[|<\s]/.test(inner) ? `(${inner})[]` : `${inner}[]`;
  }
  if (typeof coerced === 'object' && 'recordOf' in coerced) {
    const valuesType = coerced.recordOf.values ? getItemTsTypeString(coerced.recordOf.values) : 'any';
    const keys = coerced.recordOf.keys;
    // enum-constrained keys narrow the record's key union; Partial since the constraint
    // limits which keys are allowed, not which must be present
    if (keys && typeof keys === 'object' && 'enum' in keys && keys.enum.length) {
      const keyUnion = keys.enum.map((option) => JSON.stringify(String(option))).join(' | ');
      return `Partial<Record<${keyUnion}, ${valuesType}>>`;
    }
    return `Record<string, ${valuesType}>`;
  }
  return 'string';
}

const ICON_SIZE = 20;
const ICON_FETCH_TIMEOUT_MS = 2000;

let iconCacheFolderInit = false;
const iconInMemoryCache: Record<string, string> = {};
// negative cache — icons that failed to fetch (non-200, timeout, network error) are not
// retried within this process, so an offline/broken iconify doesn't stall every field
const iconFetchFailedNames = new Set<string>();

async function fetchIconSvg(
  iconifyName: string,
  color = '808080',
  iconCacheFolder = '/tmp/varlock-icon-cache',
) {
  if (!iconCacheFolderInit) {
    fs.mkdirSync(iconCacheFolder, { recursive: true });
    iconCacheFolderInit = true;
  }

  const iconPath = `${iconCacheFolder}/${iconifyName}-${ICON_SIZE}.svg`;

  let svgSrc: string | undefined;
  if (iconInMemoryCache[iconPath]) {
    svgSrc = iconInMemoryCache[iconPath];
  } else if (fs.existsSync(iconPath)) {
    const svgFileBuffer = await fs.promises.readFile(iconPath, 'utf-8');
    svgSrc = svgFileBuffer.toString();
    iconInMemoryCache[iconPath] = svgSrc;
  } else if (iconFetchFailedNames.has(iconifyName)) {
    return;
  } else {
    try {
      const iconSvg = await fetch(`https://api.iconify.design/${iconifyName.replace(':', '/')}.svg?height=${ICON_SIZE}`, {
        signal: AbortSignal.timeout(ICON_FETCH_TIMEOUT_MS),
      });
      // an error body (e.g. the literal text "404") must never be cached or embedded
      if (!iconSvg.ok) throw new Error(`icon fetch failed: ${iconSvg.status}`);
      svgSrc = await iconSvg.text();
      if (!svgSrc) throw new Error('icon fetch returned empty body');
    } catch {
      iconFetchFailedNames.add(iconifyName);
      return;
    }
    await fs.promises.writeFile(iconPath, svgSrc, 'utf-8');
    iconInMemoryCache[iconPath] = svgSrc;
  }

  const hexColor = color.startsWith('#') ? color : `#${color}`;
  const colorizedSvg = svgSrc.replaceAll('currentColor', hexColor);
  return colorizedSvg;
}

async function getTsDefinitionForField(field: ResolvedFieldType, indentLevel = 0) {
  const i = _.times(indentLevel, () => '  ').join('');
  const itemSrc = [];
  const { docs } = field;

  const jsDocLines = [];

  jsDocLines.push(`**${field.key}**${field.isSensitive ? ' 🔐 _sensitive_' : ''}`);

  if (docs.description) jsDocLines.push(...docs.description.split('\n'));

  if (docs.icon) {
    const iconSvg = await fetchIconSvg(docs.icon);
    if (iconSvg) jsDocLines.push(`![icon](data:image/svg+xml;utf-8,${encodeURIComponent(iconSvg)}) `);
  }

  if (docs.docsLinks.length) {
    jsDocLines.push('');
    docs.docsLinks.forEach((docsEntry) => {
      const docsLink = _.compact([docsEntry.url, docsEntry.description]).join(' | ');
      jsDocLines.push(`📚 {@link ${docsLink}}`);
    });
  }

  if (docs.isDeprecated) {
    jsDocLines.push('');
    jsDocLines.push(docs.deprecationMessage ? `@deprecated ${docs.deprecationMessage}` : '@deprecated');
  }

  if (jsDocLines.length === 1) {
    itemSrc.push(`/** ${jsDocLines[0]} */`);
  } else if (jsDocLines.length > 1) {
    itemSrc.push(...[
      '/**',
      ..._.flatMap(jsDocLines, (line) => [` * ${escapeBlockCommentEnd(line)}  `]),
      ' */',
    ]);
  }

  itemSrc.push(`${tsPropertyKey(field.key)}${field.isRequired ? '' : '?'}: ${getItemTsTypeString(field.coerced)};`);
  itemSrc.push('');
  return _.map(itemSrc, (line) => `${i}${line}`);
}

/**
 * How the coerced `ENV` object is exposed to TypeScript:
 * - `global` (default): globally augment `varlock/env` so `import { ENV } from 'varlock/env'` is typed.
 *   Simple, but in monorepos every in-scope package's schema merges into the one global module.
 * - `local`: export a package-local typed `ENV` from the generated file (re-exports the runtime proxy,
 *   typed to this package's schema). Defaults `processEnv`/`importMetaEnv` to `none` so nothing
 *   merges across packages (set them explicitly to re-enable). You import from the generated file
 *   (e.g. `import { ENV } from './env'`); requires a `.ts` output path (a `.d.ts` has no runtime
 *   binding, and the emitted code is TypeScript syntax so plain `.js` won't parse).
 * - `none`: emit only the type definitions, no `ENV` binding.
 */
export type TsEnvExposure = 'global' | 'local' | 'none';
/**
 * How a global env object (`process.env` / `import.meta.env`) is augmented:
 * - `loose`: typed defined keys but also allow arbitrary extra string keys.
 * - `strict`: only the defined keys are typed. (For `process.env`, `@types/node` already declares a
 *   base string index signature that declaration-merging can't remove, so this is best-effort.)
 * - `none`: don't augment it.
 */
export type TsGlobalAugment = 'strict' | 'loose' | 'none';
export type TsGenOptions = {
  exposeEnv?: TsEnvExposure;
  processEnv?: TsGlobalAugment;
  importMetaEnv?: TsGlobalAugment;
};

// defaults preserve the historical output: globally augment `varlock/env`, and augment both globals
// with only the defined keys (`strict`). Note: `@types/node` gives `process.env` a base string index
// signature we can't remove, so extra keys remain allowed there regardless.
// `exposeEnv=local` exists to keep a package's schema out of the global type scope (monorepos), so
// it defaults BOTH global augmentations to `none` — an explicit `processEnv=`/`importMetaEnv=` wins.
const DEFAULT_TS_GEN_OPTIONS = {
  exposeEnv: 'global',
  processEnv: 'strict',
  importMetaEnv: 'strict',
} satisfies Required<TsGenOptions>;

const TS_ENV_EXPOSURE_VALUES: ReadonlyArray<TsEnvExposure> = ['global', 'local', 'none'];
const TS_GLOBAL_AUGMENT_VALUES: ReadonlyArray<TsGlobalAugment> = ['strict', 'loose', 'none'];

function coerceOption<T extends string>(
  raw: unknown,
  allowed: ReadonlyArray<T>,
  fallback: T,
  optionName: string,
): T {
  if (raw === undefined || raw === null) return fallback;
  if (typeof raw === 'string' && (allowed as ReadonlyArray<string>).includes(raw)) return raw as T;
  throw new Error(`@generateTsTypes - invalid \`${optionName}\` value: ${JSON.stringify(raw)}. Allowed: ${allowed.join(', ')}`);
}

/** Parse+validate raw decorator args into resolved TS generator options. */
function resolveTsGenOptions(options: Record<string, any> = {}): Required<TsGenOptions> {
  const exposeEnv = coerceOption(options.exposeEnv, TS_ENV_EXPOSURE_VALUES, DEFAULT_TS_GEN_OPTIONS.exposeEnv, 'exposeEnv');
  // local mode = "nothing merges across packages", so global augmentations default off
  const defaultAugment = exposeEnv === 'local' ? 'none' : undefined;
  return {
    exposeEnv,
    processEnv: coerceOption(options.processEnv, TS_GLOBAL_AUGMENT_VALUES, defaultAugment ?? DEFAULT_TS_GEN_OPTIONS.processEnv, 'processEnv'),
    importMetaEnv: coerceOption(options.importMetaEnv, TS_GLOBAL_AUGMENT_VALUES, defaultAugment ?? DEFAULT_TS_GEN_OPTIONS.importMetaEnv, 'importMetaEnv'),
  };
}

// `loose` explicitly allows arbitrary extra keys via an index signature; `strict` lists only the
// defined keys. Returns the interface body suffix (either empty `{}` or `{ <index sig> }`).
function augmentBody(mode: Exclude<TsGlobalAugment, 'none'>, indent: string): string {
  if (mode === 'strict') return '{}';
  return `{\n${indent}  [key: string]: string | undefined;\n${indent}}`;
}

export async function generateTsTypesSrc(fields: Array<ResolvedFieldType>, options: TsGenOptions = {}) {
  const opts = resolveTsGenOptions(options);
  const tsSrc = [
    getAutogeneratedBanner('//'),
    '// @ts-nocheck',
    '/* eslint-disable */',
  ];

  // `local` exposure re-exports the runtime ENV proxy, so we need to import it
  if (opts.exposeEnv === 'local') {
    tsSrc.push("import { ENV as _ENV } from 'varlock/env';", '');
  }

  tsSrc.push('export type CoercedEnvSchema = {');
  for (const field of fields) {
    tsSrc.push(...await getTsDefinitionForField(field, 1));
  }
  tsSrc.push('};\n');

  const schemaHash = crypto.createHash('md5')
    .update(fields.map((f) => f.key).sort().join(','))
    .digest('hex')
    .slice(0, 8);
  const schemaAlias = `_CoercedEnvSchema_${schemaHash}`;
  const stringsAlias = `_EnvSchemaAsStrings_${schemaHash}`;

  tsSrc.push(`type ${schemaAlias} = CoercedEnvSchema;`);

  const exposedNonSensitiveKeys: Array<string> = [];
  for (const field of fields) {
    if (!field.isSensitive) exposedNonSensitiveKeys.push(field.key);
  }
  // the public schema is the non-sensitive keys picked off the full schema; when there are none
  // (every key is sensitive), `Pick<T, ''>` would be invalid — use an empty object type instead.
  // keys are grammar-constrained to [A-Za-z_][A-Za-z0-9_.-]* (no quotes/backslashes), so plain
  // single-quoting is safe — same as the rest of this emitter.
  const pickPublic = (schemaType: string) => (exposedNonSensitiveKeys.length
    ? `Pick<${schemaType}, '${exposedNonSensitiveKeys.join("' | '")}'>`
    : 'Record<string, never>');

  // globally augment `varlock/env` so `import { ENV } from 'varlock/env'` is typed
  if (opts.exposeEnv === 'global') {
    tsSrc.push(`
declare module 'varlock/env' {
  export interface TypedEnvSchema extends Readonly<${schemaAlias}> {}
  export interface PublicTypedEnvSchema extends Readonly<${pickPublic(schemaAlias)}> {}
}
`);
  }

  tsSrc.push(`
export type EnvSchemaAsStrings = {
  [Property in keyof CoercedEnvSchema]:
    CoercedEnvSchema[Property] extends string ? CoercedEnvSchema[Property]
      : (CoercedEnvSchema[Property] extends boolean ? ('true' | 'false') : string)
};
`);

  tsSrc.push(`type ${stringsAlias} = EnvSchemaAsStrings;`);

  // `local` exposure: export a package-local ENV typed to *this* package's schema.
  // runtime is identical (still the shared proxy from 'varlock/env'); types stay file-scoped
  // so nothing merges across packages — safe for monorepos.
  if (opts.exposeEnv === 'local') {
    tsSrc.push(`
export type PublicCoercedEnvSchema = Readonly<${pickPublic('CoercedEnvSchema')}>;

// re-export of the runtime ENV proxy, typed to this package's schema
export const ENV = _ENV as unknown as Readonly<CoercedEnvSchema>;
`);
  }

  const globalBlocks: Array<string> = [];
  if (opts.importMetaEnv !== 'none') {
    globalBlocks.push(`
  // add types for global import.meta.env
  interface ImportMetaEnv extends ${stringsAlias} ${augmentBody(opts.importMetaEnv, '  ')}
  interface ImportMeta {
    readonly env: ImportMetaEnv;
  }`);
  }
  if (opts.processEnv !== 'none') {
    globalBlocks.push(`
  // add types for global process.env
  namespace NodeJS {
    interface ProcessEnv extends ${stringsAlias} ${augmentBody(opts.processEnv, '    ')}
  }`);
  }

  if (globalBlocks.length) {
    tsSrc.push('declare global {', ...globalBlocks, '}');
  }

  return tsSrc.join('\n');
}
